import { NgModule } from "@angular/core";
import { NavigationStart, Router, RouterModule, Routes } from "@angular/router";
import { AuthGuard } from "./core/guards/auth-guard.service";
import { CoreMemberAccessGuard } from "./core/guards/coremember-guard.service";
import { CommunityBase } from "./shared/enums/community-base.enum";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AdminGuard } from "./core/guards/admin-guard.service";
import { AlreadyLoggedInGuard } from "./core/guards/already-logged-in-guard.service";
import { AuthIsLoadingGuard } from "./core/guards/auth-is-loading-guard.service";

const routes: Routes = [
  {
    path: "",
    canActivateChild: [AuthIsLoadingGuard], // Applied to all child routes
    children: [
      {
        path: "",
        loadComponent: () =>
          import("./modules/user-dashboard.component").then(
            (m) => m.UserDashboardComponent
          ),
      },
      {
        path: "communities",
        data: { base: CommunityBase.community },
        loadComponent: () =>
          import("./modules/communities/communities.component").then(
            (m) => m.CommunitiesComponent
          ),
      },
      {
        path: "communities/overview/:countryId",
        data: { base: CommunityBase.community },
        loadComponent: () =>
          import(
            "./modules/communities-overview/communities-overview.component"
          ).then((m) => m.CommunitiesOverviewComponent),
      },
      {
        path: "networks",
        data: { base: CommunityBase.network },
        loadComponent: () =>
          import("./modules/communities/networks.component").then(
            (m) => m.NetworksComponent
          ),
      },
      {
        path: "networks/overview/:countryId",
        data: { base: CommunityBase.network },
        loadComponent: () =>
          import(
            "./modules/communities-overview/communities-overview.component"
          ).then((m) => m.CommunitiesOverviewComponent),
      },
      {
        path: "events/event-list",
        loadComponent: () =>
          import("./modules/events/events.component").then(
            (m) => m.EventsComponent
          ),
      },
      {
        path: "events/:id",
        loadComponent: () =>
          import("./modules/event/event.component").then(
            (m) => m.EventComponent
          ),
      },
      {
        path: "event-form",
        loadComponent: () =>
          import("./modules/event/event-form/event-form.component").then(
            (m) => m.EventFormComponent
          ),
        canActivate: [AuthGuard, CoreMemberAccessGuard],
      },
      {
        path: "my-upskill",
        loadComponent: () =>
          import("./modules/my-upskill/my-upskill.component").then(
            (m) => m.MyUpskillComponent
          ),
        canActivate: [AuthGuard],
      },
      // {
      //   path: "suggestions",
      // loadComponent: () => import('./modules/suggestions/suggestions.component')
      //   .then(m => m.SuggestionsComponent),
      // // component: SuggestionsComponent,
      //   canActivate: [AuthGuard],
      // },
      {
        path: "manage",
        loadComponent: () =>
          import("./modules/manage/manage.component").then(
            (m) => m.ManageComponent
          ),
        canActivate: [AuthGuard, CoreMemberAccessGuard],
      },
      {
        path: "my-account",
        loadComponent: () =>
          import("./modules/my-account/my-account.component").then(
            (m) => m.MyAccountComponent
          ),
        canActivate: [AuthGuard],
      },
      {
        path: "admin-dashboard",
        loadComponent: () =>
          import("./modules/admin-dashboard/admin-dashboard.component").then(
            (m) => m.AdminDashboardComponent
          ),
        canActivate: [AuthGuard, AdminGuard],
      },
      {
        path: "community-form",
        loadComponent: () =>
          import(
            "./modules/community/community-form/community-form.component"
          ).then((m) => m.CommunityFormComponent),
        canActivate: [AuthGuard, AdminGuard],
      },
      {
        path: "community-form/edit/:communityId",
        loadComponent: () =>
          import(
            "./modules/community/community-form/community-form.component"
          ).then((m) => m.CommunityFormComponent),
        canActivate: [AuthGuard, CoreMemberAccessGuard],
      },
      {
        path: "community/:id",
        loadComponent: () =>
          import("./modules/community/community.component").then(
            (m) => m.CommunityComponent
          ),
      },
      {
        path: "event-form/edit/:eventId",
        data: { mode: "edit" },
        loadComponent: () =>
          import("./modules/event/event-form/event-form.component").then(
            (m) => m.EventFormComponent
          ),
        canActivate: [AuthGuard, CoreMemberAccessGuard],
      },
      {
        path: "event-form/template/:eventId",
        data: { mode: "template" },
        loadComponent: () =>
          import("./modules/event/event-form/event-form.component").then(
            (m) => m.EventFormComponent
          ),
        canActivate: [AuthGuard, CoreMemberAccessGuard],
      },
      {
        path: "event-form/:communityId",
        data: { mode: "create" },
        loadComponent: () =>
          import("./modules/event/event-form/event-form.component").then(
            (m) => m.EventFormComponent
          ),
        canActivate: [AuthGuard, CoreMemberAccessGuard],
      },
      {
        path: "event-form",
        data: { mode: "create" },
        loadComponent: () =>
          import("./modules/event/event-form/event-form.component").then(
            (m) => m.EventFormComponent
          ),
        canActivate: [AuthGuard, AdminGuard],
      },
      {
        path: "about",
        loadComponent: () =>
          import("./modules/about/about.component").then(
            (m) => m.AboutComponent
          ),
      },
      {
        path: "privacy",
        loadComponent: () =>
          import("./modules/privacy/privacy.component").then(
            (m) => m.PrivacyComponent
          ),
      },
      {
        path: "feedback",
        loadComponent: () =>
          import("./modules/feedback/feedback.component").then(
            (m) => m.FeedbackComponent
          ),
      },
      {
        path: "about/hosting-event",
        loadComponent: () =>
          import("./modules/about/hosting-event/hosting-event.component").then(
            (m) => m.HostingEventComponent
          ),
      },
      {
        path: "callback",
        loadComponent: () =>
          import("./modules/login-callback/login-callback.component").then(
            (m) => m.LoginCallbackComponent
          ),
        canActivate: [AuthGuard],
      },
      {
        path: "account/login",
        loadComponent: () =>
          import("./modules/auth-pages/login/login.component").then(
            (m) => m.LoginComponent
          ),
        canActivate: [AlreadyLoggedInGuard],
      },
      {
        path: "account/register",
        loadComponent: () =>
          import("./modules/auth-pages/register/register.component").then(
            (m) => m.RegisterComponent
          ),
        canActivate: [AlreadyLoggedInGuard],
      },
      {
        path: "account/forgot-password",
        loadComponent: () =>
          import(
            "./modules/auth-pages/forgot-password/forgot-password.component"
          ).then((m) => m.ForgotPasswordComponent),
        canActivate: [AlreadyLoggedInGuard],
      },
      // {
      //   path: "account/reset-password",
      //   component: ResetPasswordComponent,
      //   canActivate: [AlreadyLoggedInGuard],
      // },
      // {
      //   path: "account/confirmed",
      //   component: ConfirmedComponent,
      // },
      {
        path: "dashboard",
        loadComponent: () =>
          import("./modules/dashboard/dashboard.component").then(
            (m) => m.DashboardComponent
          ),
        canActivate: [AuthGuard, AdminGuard],
      },
      {
        path: "**",
        loadComponent: () =>
          import(
            "./shared/modules/page-not-found/page-not-found.component"
          ).then((m) => m.PageNotFoundComponent),
      },
      // { path: "menu", component: SidebarComponent, outlet: "sidebar" },
    ],
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: "enabled",
      anchorScrolling: "enabled",
    }),
  ],
  exports: [RouterModule],
})
export class RoutesModule {
  constructor(private router: Router) {
    this.router.events.pipe(takeUntilDestroyed()).subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (
          !event.url.startsWith("/callback") &&
          !event.url.startsWith("/silent") &&
          !event.url.includes("/account")
        ) {
          localStorage.setItem("lastVisited", event.url);
        }
      }
    });
  }
}
